"use client";

import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useAuthChange } from "@saleor/auth-sdk/react";
import { Save, Trash2, Edit3, Info } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { ProtocolModal } from "@/ui/components/calculator/protocol-modal";
import { getPeptideBySlug, getPeptideName as getPeptideNameByLocale } from "@/lib/calculator/peptide-data";
import { formatDate, type Locale } from "@/config/locale";
import { encodeCookieName } from "@/lib/auth/constants";
import type { SavedProtocolInput, SavedProtocolRecord } from "@/lib/calculator/types";
import {
	GUEST_PROTOCOLS_MAX,
	loadGuestProtocols,
	saveGuestProtocol,
	updateGuestProtocol,
	deleteGuestProtocol,
} from "@/lib/calculator/calculator-storage";

const SALEOR_API_URL = process.env.NEXT_PUBLIC_SALEOR_API_URL ?? "";

/** Check whether the Saleor access-token cookie is present (client-side). */
function hasSaleorToken(): boolean {
	if (typeof document === "undefined") return false;
	const cookieName = encodeCookieName(`${SALEOR_API_URL}+saleor_auth_access_token`);
	return document.cookie.includes(cookieName);
}

interface CurrentValues {
	peptideSlug: string;
	peptideAmount: number;
	bacWater: number;
	dose: number;
	syringeSize: number;
	titrationWeek?: number;
}

interface SavedProtocolsProps {
	locale: string;
	channel: string;
	currentValues?: CurrentValues;
	onLoadProtocol?: (protocol: SavedProtocolRecord) => void;
}

export function SavedProtocols({ locale, currentValues, onLoadProtocol }: SavedProtocolsProps) {
	const t = useTranslations("peptideCalculator");
	const tCommon = useTranslations("common");
	// Initialise from cookie so there's no flash — lazy initialiser avoids SSR mismatch
	const [isSignedIn, setIsSignedIn] = useState<boolean>(() => hasSaleorToken());

	useAuthChange({
		saleorApiUrl: SALEOR_API_URL,
		onSignedIn: () => setIsSignedIn(true),
		onSignedOut: () => setIsSignedIn(false),
	});

	const [protocols, setProtocols] = useState<SavedProtocolRecord[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const [saveModalOpen, setSaveModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editingProtocol, setEditingProtocol] = useState<SavedProtocolRecord | null>(null);
	const [saving, setSaving] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const fetchProtocols = useCallback(async () => {
		setLoading(true);
		setLoadError(false);
		try {
			const res = await fetch("/api/calculator/protocols");
			if (!res.ok) throw new Error("Protocol fetch failed");
			const data = (await res.json()) as { protocols: SavedProtocolRecord[] };
			if (!Array.isArray(data.protocols)) throw new Error("Invalid protocol response");
			setProtocols(data.protocols);
		} catch {
			setLoadError(true);
		} finally {
			setLoading(false);
		}
	}, []);

	// Account path fetches from the API; guest path reads localStorage synchronously.
	// Re-runs whenever auth state flips so a fresh login/logout swaps the source.
	useEffect(() => {
		if (isSignedIn) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-auth-change: fetchProtocols() sets a loading flag synchronously before the network round-trip; the fetch itself must live in an effect. Guest branch below stays clean (one diagnostic per effect); its localStorage read is hydration-safe only inside this effect
			void fetchProtocols();
		} else {
			setLoadError(false);
			setProtocols(loadGuestProtocols());
		}
	}, [isSignedIn, fetchProtocols]);

	const maxReached = protocols.length >= GUEST_PROTOCOLS_MAX;

	const handleSaveNew = async (name: string, notes: string) => {
		if (!currentValues) return;
		const input: SavedProtocolInput = {
			name,
			notes: notes || undefined,
			peptideSlug: currentValues.peptideSlug,
			peptideAmount: currentValues.peptideAmount,
			bacWater: currentValues.bacWater,
			dose: currentValues.dose,
			syringeSize: currentValues.syringeSize,
			titrationWeek: currentValues.titrationWeek,
		};

		// Guest path: persist to localStorage (own key only) — no network.
		if (!isSignedIn) {
			setProtocols(saveGuestProtocol(input));
			setSaveModalOpen(false);
			return;
		}

		setSaving(true);
		try {
			const res = await fetch("/api/calculator/protocols", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(input),
			});
			if (res.ok) {
				setSaveModalOpen(false);
				await fetchProtocols();
			}
		} catch {
			// silently ignore
		} finally {
			setSaving(false);
		}
	};

	const handleEdit = async (name: string, notes: string) => {
		if (!editingProtocol) return;

		// Guest path: update in localStorage.
		if (!isSignedIn) {
			setProtocols(updateGuestProtocol(editingProtocol.id, { name, notes: notes || undefined }));
			setEditModalOpen(false);
			setEditingProtocol(null);
			return;
		}

		setSaving(true);
		try {
			const res = await fetch(`/api/calculator/protocols/${editingProtocol.id}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name, notes: notes || null }),
			});
			if (res.ok) {
				setEditModalOpen(false);
				setEditingProtocol(null);
				await fetchProtocols();
			}
		} catch {
			// silently ignore
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id: string) => {
		// Guest path: drop from localStorage.
		if (!isSignedIn) {
			setProtocols(deleteGuestProtocol(id));
			return;
		}

		setDeletingId(id);
		try {
			const res = await fetch(`/api/calculator/protocols/${id}`, { method: "DELETE" });
			if (res.ok) {
				setProtocols((prev) => prev.filter((p) => p.id !== id));
			}
		} catch {
			// silently ignore
		} finally {
			setDeletingId(null);
		}
	};

	const openEdit = (protocol: SavedProtocolRecord) => {
		setEditingProtocol(protocol);
		setEditModalOpen(true);
	};

	const getPeptideName = (slug: string) => {
		const peptide = getPeptideBySlug(slug);
		if (!peptide) return slug;
		return getPeptideNameByLocale(peptide, locale as Locale);
	};

	const formatProtocolDate = (dateStr: string) => {
		try {
			return formatDate(
				new Date(dateStr),
				{
					day: "numeric",
					month: "short",
					year: "numeric",
				},
				locale as Locale,
			);
		} catch {
			return dateStr;
		}
	};

	// Per-record meta line. Titration plans have NO single dose — show the start date
	// (the schedule anchor) instead of a "· X mg" dose. Single protocols keep the
	// classic peptide · dose · saved-date line, unchanged.
	const protocolMeta = (protocol: SavedProtocolRecord): string => {
		const peptide = getPeptideName(protocol.peptideSlug);
		if (protocol.kind === "titration") {
			const start = protocol.startDate
				? formatProtocolDate(protocol.startDate)
				: formatProtocolDate(protocol.updatedAt);
			return `${peptide} · ${start}`;
		}
		return `${peptide} · ${protocol.dose} mg · ${formatProtocolDate(protocol.updatedAt)}`;
	};

	return (
		<div className="space-y-4" aria-busy={loading}>
			{/* Header */}
			<div className="flex items-center justify-between">
				<h3 className="text-base font-semibold text-foreground">{t("protocols.title")}</h3>
				{currentValues && !maxReached && (
					<Button
						variant="outline-solid"
						size="sm"
						onClick={() => setSaveModalOpen(true)}
						disabled={saving}
						className="gap-2"
					>
						<Save className="h-4 w-4" />
						{t("protocols.saveCurrent")}
					</Button>
				)}
			</div>

			{/* Max reached warning */}
			{maxReached && <p className="text-sm text-warning">{t("protocols.maxReached")}</p>}

			{/* Loading skeleton */}
			{loading && (
				<div className="tool-protocol-skeleton space-y-2" aria-hidden="true">
					{[0, 1, 2].map((i) => (
						<div key={i} className="h-16 rounded-lg bg-muted" />
					))}
				</div>
			)}

			{!loading && (
				<div className="tool-protocol-reveal">
					{/* Empty state */}
					{loadError && (
						<p
							role="alert"
							className="border-destructive/30 bg-destructive/5 rounded-xl border p-4 text-sm text-destructive"
						>
							{tCommon("error")}
						</p>
					)}
					{!loadError && protocols.length === 0 && (
						<div className="rounded-xl border border-dashed border-border p-6 text-center">
							<p className="text-sm text-muted-foreground">{t("protocols.empty")}</p>
						</div>
					)}

					{/* Protocol list */}
					{protocols.length > 0 && (
						<div className="space-y-2">
							{protocols.map((protocol) => (
								<div
									key={protocol.id}
									className="hover:border-primary/30 hover:bg-card/80 group flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5 transition-colors"
								>
									{/* Clickable area — loads the protocol back into the calculator */}
									<button
										type="button"
										className="flex min-h-11 flex-1 flex-col justify-center text-left"
										onClick={() => onLoadProtocol?.(protocol)}
									>
										<p className="flex items-center gap-2 text-sm font-medium text-foreground">
											<span className="min-w-0 truncate">{protocol.name}</span>
											{protocol.kind === "titration" && (
												<span className="bg-warning/15 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
													{t("protocols.titrationBadge")}
												</span>
											)}
										</p>
										<p className="mt-0.5 font-mono text-xs tabular-nums text-muted-foreground">
											{protocolMeta(protocol)}
										</p>
										{protocol.notes && (
											<p className="text-muted-foreground/80 mt-1 line-clamp-2 text-xs">{protocol.notes}</p>
										)}
									</button>

									{/* Actions — always visible on touch, revealed on hover for pointer devices.
							    44px hit targets. */}
									<div className="flex shrink-0 items-center gap-1 opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100">
										<button
											type="button"
											onClick={() => openEdit(protocol)}
											className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
											aria-label={t("protocols.edit")}
										>
											<Edit3 className="h-4 w-4" />
										</button>
										<button
											type="button"
											onClick={() => void handleDelete(protocol.id)}
											disabled={deletingId === protocol.id}
											className="hover:bg-destructive/10 flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											aria-label={t("protocols.delete")}
										>
											<Trash2 className="h-4 w-4" />
										</button>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			)}

			{/* Storage hint — guests are local-only, signed-in protocols sync across devices. */}
			<p className="flex items-start gap-1.5 text-xs text-muted-foreground">
				<Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
				<span>{isSignedIn ? t("protocols.syncHintAccount") : t("protocols.syncHintGuest")}</span>
			</p>

			{/* Save new modal */}
			<ProtocolModal
				open={saveModalOpen && !saving}
				onClose={() => setSaveModalOpen(false)}
				onSave={(name, notes) => void handleSaveNew(name, notes)}
			/>

			{/* Edit modal */}
			<ProtocolModal
				open={editModalOpen && !saving}
				onClose={() => {
					setEditModalOpen(false);
					setEditingProtocol(null);
				}}
				onSave={(name, notes) => void handleEdit(name, notes)}
				initialName={editingProtocol?.name}
				initialNotes={editingProtocol?.notes ?? ""}
				isEdit
			/>
		</div>
	);
}
